import { DateTime } from "luxon";
import { getDefaultLocaleConfig, isDevelopmentMode } from "../configuration/configuration";
import {
  IAggregate,
  IDictionary,
  IFilter,
  IFilterValues,
  IParentSize,
  IRealtimeData,
  ISelectedNode,
  Order
} from "../types";
import * as htmlToImage from "html-to-image";
import JSZip from "jszip";
import * as mathJS from "mathjs";
import { useEffect, useState } from "react";

export const PollInterval5Min = 300000;  // 5 min

export const logger = (...args: any[]): void => {
  if (isDevelopmentMode()) {
    console.log(`[${DateTime.now().toFormat("MM-dd HH:mm:ss")}]`, ...args);
  }
};

export const loggerError = (...args: any[]): void => {
  if (isDevelopmentMode()) {
    console.error(`[${DateTime.now().toFormat("MM-dd HH:mm:ss")}]`, ...args);
  }
};

export const getUID = (): string => {
  const head = Date.now().toString(36);
  const tail = Math.random().toString(36).substring(2);

  return head + tail;
};

export const getCurrentDatetimeInSeconds = (): number => {

  return DateTime.now().toSeconds();
  // const millisecondsBySecond = 1000;
  //
  // const node = getStore().getState().selectedNode;
  // if (node.timezone) {
  //   const now = DateTime.local().setZone(node.timezone);
  //   const strDate = `${now.year}-${now.month}-${now.day} ${now.hour}:${now.minute}:${now.second}+00:00`;
  //
  //   return Date.parse(strDate) / millisecondsBySecond;
  // }
  //
  // const dateTime = new Date();
  //
  // return new Date(dateTime.getTime() - dateTime.getTimezoneOffset() * 60000).getTime() / millisecondsBySecond;
};

export const formatDate = (datetime: number | null): string => {
  return datetime ? DateTime.fromSeconds(datetime).toFormat(getDefaultLocaleConfig().dateFormat) : "---";
};

export const formatDay = (datetime: number | null): string => {
  return datetime ? DateTime.fromSeconds(datetime).day.toString() : "---";
};

export const formatDateTimeWithSeconds = (datetime: number | null): string => {
  return datetime ? DateTime.fromSeconds(datetime).toFormat(`${getDefaultLocaleConfig().dateFormat} ${getDefaultLocaleConfig().timeFormat}`) : "---";
};

export const formatDateTimeWithSecondsX = (datetime: number | null): string => {
  // const offset = datetime ? DateTime.fromSeconds(datetime).offset : 0;

  return datetime ? DateTime.fromSeconds(datetime).toFormat(`${getDefaultLocaleConfig().dateFormat} ${getDefaultLocaleConfig().timeFormat}`) : "---";
};

export const formatTimeWithSeconds = (datetime: number | null): string => {
  return datetime ? DateTime.fromSeconds(datetime).toFormat(getDefaultLocaleConfig().timeFormat) : "---";
};

export const formatDateTimeWithSecondsExport = (datetime: number | null): string => {
  return datetime ? DateTime.fromSeconds(datetime).toFormat("yyyy-MM-dd HH:mm:ss.SSS") : "---";
};

export const formatNumber = (value: number, factionDigits: number): string => {
  const base10 = 10;
  const factor = base10 ** factionDigits;

  return (Math.trunc(value * factor) / factor).toFixed(factionDigits);
};

export const toTons = (lbs: number, short = true): number => {
  const factorTons = short ? 2000 : 2205;

  return lbs / factorTons;
};

export const stableSort = <T>(array: T[], comparator: (a: T, b: T) => number): T[] => {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }

    return a[1] - b[1];
  });

  return stabilizedThis.map((el) => el[0]);
};

export const sortText = (a: string, b: string): number => {
  if (a > b) {
    return 1;
  }

  return b > a ? -1 : 0;
};

export const roundUp = (value: number, factionDigits: number): number => {
  const base10 = 10;
  const factor = base10 ** factionDigits;

  return (Math.round(value / factor) + 1 ) * factor;
};

export const getCurrentWeekRange = (dateTime: DateTime | null = null): DateTime[] => {
  const to = dateTime ?? DateTime.now();
  const from = to.minus({ days: to.weekday - 1 });

  return [ from, to ];
};


export const getLastWeekRange = (dateTime: DateTime | null = null): DateTime[] => {
  const datetime = dateTime ?? DateTime.now();

  const to = datetime.minus({ days: datetime.weekday });
  const from = to.minus({ days: 6 });

  return [ from, to ];
};

export const getLast7DaysRange = (dateTime: DateTime | null = null): DateTime[] => {
  const to = dateTime ?? DateTime.now();
  const from = to.minus({ days: 6 });

  return [ from, to ];
};

export const getCurrentMonthRange = (dateTime: DateTime | null = null): DateTime[] => {
  const to = dateTime ?? DateTime.now();
  const from = to.minus({ days: to.day - 1 });

  return [ from, to ];
};

export const getLastMonthRange = (dateTime: DateTime | null = null): DateTime[] => {
  const datetime = dateTime ?? DateTime.now();

  const dt = datetime.minus({ days: datetime.day });
  const from = dt.minus({ days: dt.day - 1 });

  return [ from, dt ];
};

export const getLast30DaysRange = (dateTime: DateTime | null = null): DateTime[] => {
  const to = dateTime ?? DateTime.now();
  const from = to.minus({ days: 30 });

  return [ from, to ];
};

export const getCurrentDayRange = (): DateTime[] => {
  const to = DateTime.now();

  return [ to, to ];
};


export const getDefaultFilterValuesByFactoryDay = (): IFilterValues => {
  const [ selectedFrom, selectedTo ] = getCurrentDayRange();

  return {
    mode: "FACTORY_DAY",
    crew: "ALL",
    shift: "ALL",
    selectedFrom: selectedFrom.toISODate()!,
    selectedTo: selectedTo.toISODate()!
  };
};

export const getDefaultFilterValuesByCurrentMonth = (): IFilterValues => {
  const [ selectedFrom, selectedTo ] = getCurrentMonthRange();

  return {
    mode: "CURRENT_MONTH",
    crew: "ALL",
    shift: "ALL",
    selectedFrom: selectedFrom.toISODate()!,
    selectedTo: selectedTo.toISODate()!
  };
};

export const getRemoteFilter = (filterValues: IFilterValues, selectedNode: ISelectedNode): IFilter => {
  let newFilter: IFilter = {
    nodeID: selectedNode.nodeID,
    nodeType: selectedNode.type,
    crew: filterValues.crew,
    shift: filterValues.shift
  };

  switch (filterValues.mode) {
    case "FACTORY_DAY":
      newFilter = {
        ...newFilter,
        factoryDay: filterValues.selectedTo
      };
      break;

    case "REAL_TIME":
      break;

    default:
      newFilter = {
        ...newFilter,
        fromDate: filterValues.selectedFrom,
        toDate: filterValues.selectedTo
      };
      break;
  }

  return newFilter;
};

export const rowsPerPageOptions = [10, 25, 100];

export const descendingComparator = <T>(a: T, b: T, orderBy: keyof T): number => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }

  return 0;
};


export const getComparator = <T, Key extends keyof T>(
  order: Order,
  orderBy: Key,
): (a: T, b: T) => number => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};


export const cloneObject = <T>(object: T): T => {
  return JSON.parse(JSON.stringify(object)) as T;
};


export const distinct = <T>(value: T, index: number, self: T[]): boolean => {
  return self.indexOf(value) === index;
};

export const saveBlobAs = (blob: string, fileName: string): void =>{
  const elem = window.document.createElement("a");
  elem.href = blob;
  elem.download = fileName;
  elem.style.display = "none";
  (document.body || document.documentElement).appendChild(elem);
  if (typeof elem.click === "function") {
    elem.click();
  } else {
    elem.target = "_blank";
    elem.dispatchEvent(new MouseEvent("click", {
      view: window,
      bubbles: true,
      cancelable: true
    }));
  }

  URL.revokeObjectURL(elem.href);
  elem.remove();
};

export const onCapture = async (id: string, filename: string): Promise<void> => {

  return new Promise(resolve => {
    const element = document.getElementById(id);

    if (element) {
      htmlToImage.toPng(element)
        .then(dataUrl => {
          saveBlobAs(dataUrl, `${filename}.png`);
          resolve();
        });
    }
  });
};


export const saveZipAs = async (objectURL: IDictionary<Blob>, filename: string): Promise<void> =>{
  const elem = window.document.createElement("a");

  const zip = new JSZip();

  const entries = Object.entries(objectURL);
  for (const [ key, data ] of entries) {
    zip.file(`${key}.csv`, data, { base64: false });
  }

  const base64 = await zip.generateAsync({ type: "base64" });

  elem.href = "data:application/zip;base64," + base64;
  elem.download = filename;
  elem.style.display = "none";
  (document.body || document.documentElement).appendChild(elem);
  if (typeof elem.click === "function") {
    elem.click();
  } else {
    elem.target = "_blank";
    elem.dispatchEvent(new MouseEvent("click", {
      view: window,
      bubbles: true,
      cancelable: true
    }));
  }

  URL.revokeObjectURL(elem.href);
  elem.remove();
};

// export const requestFullscreen = (): void => {
//   const elem = document.getElementById("ContentAreaID") as any; //  as any;
//
//   if (elem && elem.requestFullscreen) {
//     elem.requestFullscreen().then();
//   } else if (elem && elem.mozRequestFullScreen) {
//     elem.mozRequestFullScreen().then();
//   } else if (elem && elem.webkitRequestFullscreen) {
//     elem.webkitRequestFullscreen().then();
//   } else if (elem && elem.msRequestFullscreen) {
//     elem.msRequestFullscreen().then();
//   }
// };


export const canRequestFullscreen = (): boolean => {
  const elem = document.getElementById("ContentAreaID") as any;

  return elem && (elem.requestFullscreen || elem.webkitRequestFullscreen || elem.msRequestFullscreen);
};

export const requestFullscreen = (): void => {
  const elem = document.getElementById("root") as any;

  if (elem.requestFullscreen) {
    elem.requestFullscreen();
  } else if (elem.webkitRequestFullscreen) { /* Safari */
    elem.webkitRequestFullscreen();
  } else if (elem && elem.msRequestFullscreen) {
    elem.msRequestFullscreen().then();
  }
};


export const exitFullscreen = (): void => {
  if (document.exitFullscreen) {
    document.exitFullscreen().then();
  } else if ((document as any).webkitExitFullscreen) { /* Safari */
    (document as any).webkitExitFullscreen();
  }
};


export const getFloatValue = (rtData: IRealtimeData | undefined, optional: number): number => {
  if (!rtData) {
    return optional;
  }

  if (!rtData.Type || rtData.Type === "N") {
    return rtData.Value as number;
  }

  if (rtData.Type === "B") {
    return rtData.Value ? 1 : 0;
  }

  return parseFloat(`${rtData.Value}`);
};



export const useWindowDimensions = (): IParentSize => {
  const [dimension, setDimension] = useState<IParentSize>({ width: window.innerWidth, height: window.innerHeight });

  const updateWidthAndHeight = () => {
    setDimension({ width: window.innerWidth, height: window.innerHeight });
  };

  useEffect(() => {
    window.addEventListener("resize", updateWidthAndHeight);

    return () => window.removeEventListener("resize", updateWidthAndHeight);
  });

  return dimension;
};

export const exportFile = (base64: string, filename: string): void => {
  const elem = window.document.createElement("a");
  elem.href = base64;
  elem.download = filename;
  elem.style.display = "none";

  (document.body || document.documentElement).appendChild(elem);
  if (typeof elem.click === "function") {
    elem.click();
  } else {
    elem.target = "_blank";
    elem.dispatchEvent(new MouseEvent("click", {
      view: window,
      bubbles: true,
      cancelable: true
    }));
  }

  URL.revokeObjectURL(elem.href);
  elem.remove();
};

// export const aggregate = (initValue: number): IAggregate => {
//   const data: number[] = [ initValue ];
//
//   return {
//     add: (value: number) => data.push(value),
//     min: () => Math.min(...data),
//     max: () => Math.max(...data),
//     sum: () => data.reduce((previousValue, currentValue) => previousValue + currentValue),
//     avg: () => data.length > 0
//       ? data.reduce((previousValue, currentValue) => previousValue + currentValue) / data.length
//       : 0
//   };
// };

export const aggregate = (initValue?: number): IAggregate => {
  const data: number[] = initValue ? [ initValue ] : [];

  return {
    add: (value: number) => data.push(value),
    min: () => Math.min(...data),
    max: () => Math.max(...data),
    sum: () => mathJS.sum(data),
    count: () => data.length,
    avg: () => {
      try {
        if (data.length > 1) {
          return mathJS.mean(...data);
        }
      } catch (e: unknown) {
        console.error(e);
      }

      return Number.MIN_VALUE;
    },
    stdDev: () => mathJS.std(...data)
  };
};